import React, { useContext } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import anatomyImg1 from '../../../assets/images/CanineMuscularAnatomy1.png';
import anatomyImg2 from '../../../assets/images/CanineMuscularAnatomy2.png';
import anatomyImg3 from '../../../assets/images/CanineMuscularAnatomy3.png';
import helpHubImg from '../../../assets/images/HelpHub.svg';
import arrowRightIcon from '../../../assets/icons/ArrowRight.svg';
import { anatomyStuffPostersUrl } from '../../../config';

function PanelButton({ label, onClick }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '16.78px',
                background: '#FF669B',
                borderRadius: '223px',
                pl: { xs: '16px', md: '22px' },
                pr: { xs: '6px', md: '8px' },
                py: { xs: '6px', md: '8px' },
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { background: '#FF85B0' },
            }}
        >
            <Typography
                sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: { xs: '14px', md: '18px' },
                    lineHeight: 'normal',
                    letterSpacing: { xs: '-0.28px', md: '-0.36px' },
                    color: '#FFFFFF',
                    whiteSpace: 'nowrap',
                }}
            >
                {label}
            </Typography>
            <Box
                component="img"
                src={arrowRightIcon}
                alt=""
                sx={{ width: { xs: 32, md: 40 }, height: { xs: 32, md: 40 }, display: 'block' }}
            />
        </Box>
    );
}

const TITLE_NAVY = {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 600,
    fontSize: { xs: '24px', md: '40px' },
    lineHeight: { xs: '28px', md: '40px' },
    letterSpacing: { xs: '-0.6px', md: '-1.6px' },
    color: '#000453',
    position: 'absolute',
    top: { xs: '16px', md: '20px' },
    left: { xs: '16px', md: '20px' },
    zIndex: 2,
};

function DashboardPromoPanel() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { userDataStore } = useContext(StoreContext);
    const navigate = useNavigate();
    const isFree = userDataStore?.subscriptionData?.accountType === 'Free';

    const anatomyTitle = isMobile
        ? (isFree ? 'Browse posters' : '10% off posters')
        : (isFree ? 'Browse AnatomyStuff posters' : '10% off AnatomyStuff posters');
    const anatomyButtonLabel = isFree ? 'Shop now' : 'Use code CETRA10 at checkout';
    const handleAnatomyClick = () => window.open(anatomyStuffPostersUrl, '_blank');

    return (
        <Box
            sx={{
                maxWidth: '1440px',
                margin: '0 auto',
                width: '100%',
                px: { xs: '20px', md: '40px' },
                pb: { xs: '16px', md: '21px' },
                boxSizing: 'border-box',
                display: 'flex',
                gap: { xs: '16px', md: '21px' },
                flexDirection: { xs: 'column', md: 'row' },
                height: { xs: 'auto', md: '408px' },
            }}
        >
            {/* AnatomyStuff posters panel */}
            <Box
                sx={{
                    flex: { xs: 'none', md: 2 },
                    height: { xs: '352px', md: '100%' },
                    borderRadius: { xs: '24px', md: '30px' },
                    background: '#F0F0FC',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Typography sx={TITLE_NAVY}>{anatomyTitle}</Typography>

                {/* Mobile: single centered anatomy image */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '72px',
                        left: '16px',
                        right: '16px',
                        bottom: '60px',
                        zIndex: 1,
                        display: { xs: 'flex', md: 'none' },
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <img
                        src={anatomyImg1}
                        alt=""
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            objectPosition: 'center',
                            pointerEvents: 'none',
                            display: 'block',
                        }}
                    />
                </Box>

                {/* Desktop: 3 anatomy images side by side */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '97px',
                        left: '42px',
                        right: '42px',
                        bottom: 0,
                        display: { xs: 'none', md: 'flex' },
                        gap: '22px',
                        zIndex: 1,
                    }}
                >
                    {[anatomyImg1, anatomyImg2, anatomyImg3].map((src, i) => (
                        <Box key={i} sx={{ flex: 1, minWidth: 0 }}>
                            <img
                                src={src}
                                alt=""
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    objectPosition: 'top center',
                                    pointerEvents: 'none',
                                    display: 'block',
                                }}
                            />
                        </Box>
                    ))}
                </Box>

                <Box sx={{ position: 'absolute', bottom: { xs: '16px', md: '20px' }, left: { xs: '16px', md: '20px' }, zIndex: 2 }}>
                    <PanelButton label={anatomyButtonLabel} onClick={handleAnatomyClick} />
                </Box>
            </Box>

            {/* Help hub panel */}
            <Box
                sx={{
                    flex: { xs: 'none', md: 1 },
                    height: { xs: '352px', md: '100%' },
                    borderRadius: { xs: '24px', md: '30px' },
                    background: '#F0F0FC',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Typography sx={TITLE_NAVY}>Help hub</Typography>

                <Box
                    component="img"
                    src={helpHubImg}
                    alt="Help hub"
                    sx={{
                        position: 'absolute',
                        top: { xs: '86px', md: '106px' },
                        left: { xs: '48px', md: '93px' },
                        right: { xs: '48px', md: '93px' },
                        bottom: { xs: '86px', md: '106px' },
                        width: { xs: 'calc(100% - 96px)', md: 'calc(100% - 186px)' },
                        height: { xs: 'calc(100% - 172px)', md: 'calc(100% - 212px)' },
                        objectFit: 'contain',
                        objectPosition: 'center',
                        pointerEvents: 'none',
                        zIndex: 1,
                    }}
                />

                <Box sx={{ position: 'absolute', bottom: { xs: '16px', md: '20px' }, left: { xs: '16px', md: '20px' }, zIndex: 2 }}>
                    <PanelButton label="Learn more" onClick={() => navigate('/help-hub')} />
                </Box>
            </Box>
        </Box>
    );
}

export default observer(DashboardPromoPanel);
